/**
 * §12.1 — Audio graph & buses. AudioContext bootstrap, first-gesture unlock, master
 * limiter, procedural reverb + delay sends. Presentation only: never reads or writes sim
 * state, never consumes a gameplay RNG stream (spine §7).
 *
 *   sources → family bus → group bus (sfx / music / ambient) → master → limiter → destination
 *   family bus → family reverb send → convolver → reverb return → master
 *   music lead  → delay send → delay + feedback → delay return → master
 */

/** Spine §13 — 48 kHz where the device allows. */
export const AUDIO_SAMPLE_RATE = 48000;

/** §12.1 hard ceiling −6 dBFS, expressed linear. */
export const MASTER_CEILING_LINEAR = 0.5;

/** §12.5 — a volume change must be audible within 50 ms. */
export const VOLUME_RAMP_S = 0.02;

/** §12.6 pause rule (3): music holds at −12 dB while paused. */
export const PAUSE_MUSIC_LINEAR = 0.251;

export const FAMILY_NAMES = [
  'weapons',
  'enemy',
  'pickups',
  'levelup',
  'boss',
  'warnings',
  'player_damage',
  'ui',
  'music',
  'ambient',
] as const;

export type FamilyName = (typeof FAMILY_NAMES)[number];
export type GroupName = 'sfx' | 'music' | 'ambient';

export const FAMILY_GROUP: Readonly<Record<FamilyName, GroupName>> = {
  weapons: 'sfx',
  enemy: 'sfx',
  pickups: 'sfx',
  levelup: 'sfx',
  boss: 'sfx',
  warnings: 'sfx',
  player_damage: 'sfx',
  ui: 'sfx',
  music: 'music',
  ambient: 'ambient',
};

/** §12.6 headroom rule — family bus gain ≤ 0.8. */
export const FAMILY_BUS_GAIN: Readonly<Record<FamilyName, number>> = {
  weapons: 0.62,
  enemy: 0.55,
  pickups: 0.6,
  levelup: 0.7,
  boss: 0.72,
  warnings: 0.8,
  player_damage: 0.78,
  ui: 0.5,
  music: 0.55,
  ambient: 0.3,
};

/** Reverb send per family — the abyss needs space, warnings and UI stay dry for clarity. */
export const FAMILY_REVERB_SEND: Readonly<Record<FamilyName, number>> = {
  weapons: 0.12,
  enemy: 0.1,
  pickups: 0.18,
  levelup: 0.22,
  boss: 0.3,
  warnings: 0.05,
  player_damage: 0.1,
  ui: 0.03,
  music: 0.24,
  ambient: 0.38,
};

export interface AudioGraph {
  ctx: AudioContext;
  /** Post user-volume, pre-limiter. */
  master: GainNode;
  limiter: DynamicsCompressorNode;
  groups: Record<GroupName, GainNode>;
  families: Record<FamilyName, GainNode>;
  familySends: Record<FamilyName, GainNode>;
  convolver: ConvolverNode;
  reverbReturn: GainNode;
  /** Short slap delay — the music lead taps this directly. */
  delaySend: GainNode;
  delay: DelayNode;
  delayFeedback: GainNode;
  delayReturn: GainNode;
  /** 2 s white noise, baked once, deterministic (§12.4 "noise"). */
  noise: AudioBuffer;
  /** 2 s brown noise for R-POUR / trench bed. */
  brown: AudioBuffer;
}

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

function resolveAudioContextCtor(): AudioContextCtor | null {
  if (typeof globalThis === 'undefined') return null;
  const w = globalThis as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/** Deterministic noise source for baked buffers. Fixed seed — never a world stream. */
function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return function next(): number {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeWhiteNoise(ctx: BaseAudioContext, seconds: number, seed: number): AudioBuffer {
  const frames = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
  const d = buf.getChannelData(0);
  const rnd = makeRng(seed);
  for (let i = 0; i < frames; i++) d[i] = rnd() * 2 - 1;
  return buf;
}

function makeBrownNoise(ctx: BaseAudioContext, seconds: number, seed: number): AudioBuffer {
  const frames = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
  const d = buf.getChannelData(0);
  const rnd = makeRng(seed);
  let last = 0;
  for (let i = 0; i < frames; i++) {
    const white = rnd() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    d[i] = last * 3.5;
  }
  return buf;
}

/**
 * Procedural impulse response: decaying noise with a short early-reflection cluster.
 * No audio asset is fetched — the abyss space is synthesized (spine §8).
 */
export function makeImpulseResponse(ctx: BaseAudioContext, seconds: number, decay: number, seed: number): AudioBuffer {
  const rate = ctx.sampleRate;
  const frames = Math.max(1, Math.floor(rate * seconds));
  const buf = ctx.createBuffer(2, frames, rate);
  const rnd = makeRng(seed);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < frames; i++) {
      const t = i / frames;
      d[i] = (rnd() * 2 - 1) * Math.pow(1 - t, decay);
    }
    // Early reflections: four sparse taps in the first 40 ms give the tail a room.
    for (let k = 0; k < 4; k++) {
      const idx = Math.floor(rate * (0.006 + k * 0.009 + ch * 0.0017));
      if (idx < frames) d[idx] += (k % 2 === 0 ? 0.55 : -0.45) * (1 - k * 0.2);
    }
  }
  return buf;
}

/**
 * Owns the AudioContext lifecycle. Constructing this before any user gesture is safe:
 * nothing is created until `initSync` runs, and nothing sounds until the context resumes.
 */
export class AudioHost {
  graph: AudioGraph | null = null;
  private initializing: Promise<void> | null = null;
  private gestureInstalled = false;
  private focusInstalled = false;
  private disposed = false;
  private muted = false;
  private paused = false;
  private volMaster = 0.8;
  private volMusic = 0.7;
  private volSfx = 0.8;
  /** Set true once the context has actually run past a user gesture. */
  unlocked = false;

  private readonly onGesture = (): void => {
    // iOS Safari only unlocks when resume() is called synchronously inside the handler.
    this.initSync();
    const g = this.graph;
    if (g) {
      if (g.ctx.state !== 'running') void g.ctx.resume().catch(noop);
      // Silent one-sample buffer: the canonical iOS unlock kick.
      const src = g.ctx.createBufferSource();
      src.buffer = g.ctx.createBuffer(1, 1, g.ctx.sampleRate);
      src.connect(g.ctx.destination);
      src.start(0);
      this.unlocked = true;
    }
    this.removeGestureListeners();
  };

  private readonly onFocusChange = (): void => {
    // §12.6 silence rule (4) / spine §14 — focus loss mutes the master.
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    this.applyMasterGain(hidden);
  };

  get ctx(): AudioContext | null {
    return this.graph ? this.graph.ctx : null;
  }

  get now(): number {
    return this.graph ? this.graph.ctx.currentTime : 0;
  }

  get sampleRate(): number {
    return this.graph ? this.graph.ctx.sampleRate : AUDIO_SAMPLE_RATE;
  }

  /** Idempotent, safe before any gesture. Resolves once the graph exists. */
  init(): Promise<void> {
    if (this.disposed) return Promise.resolve();
    if (this.graph && this.graph.ctx.state === 'running') return Promise.resolve();
    if (this.initializing) return this.initializing;
    this.initializing = (async () => {
      this.initSync();
      const g = this.graph;
      if (g && g.ctx.state === 'suspended') {
        try {
          await g.ctx.resume();
          this.unlocked = true;
        } catch {
          // Autoplay policy: stays suspended until a gesture arrives. Not an error.
        }
      } else if (g) {
        this.unlocked = true;
      }
      this.initializing = null;
    })();
    return this.initializing;
  }

  /** Builds the whole node graph synchronously. Safe to call from inside a gesture handler. */
  initSync(): void {
    if (this.graph || this.disposed) return;
    const Ctor = resolveAudioContextCtor();
    if (!Ctor) return;
    let ctx: AudioContext;
    try {
      ctx = new Ctor({ sampleRate: AUDIO_SAMPLE_RATE, latencyHint: 'interactive' });
    } catch {
      try {
        ctx = new Ctor();
      } catch {
        return;
      }
    }

    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -9;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.1;
    limiter.connect(ctx.destination);

    const master = ctx.createGain();
    master.gain.value = this.volMaster * MASTER_CEILING_LINEAR;
    master.connect(limiter);

    const groups = {
      sfx: ctx.createGain(),
      music: ctx.createGain(),
      ambient: ctx.createGain(),
    } as Record<GroupName, GainNode>;
    groups.sfx.gain.value = this.volSfx;
    groups.music.gain.value = this.volMusic;
    groups.ambient.gain.value = this.volSfx;
    groups.sfx.connect(master);
    groups.music.connect(master);
    groups.ambient.connect(master);

    const convolver = ctx.createConvolver();
    convolver.buffer = makeImpulseResponse(ctx, 2.4, 2.8, 0x4c554d45);
    const reverbReturn = ctx.createGain();
    reverbReturn.gain.value = 0.55;
    convolver.connect(reverbReturn);
    reverbReturn.connect(master);

    const delaySend = ctx.createGain();
    delaySend.gain.value = 1;
    const delay = ctx.createDelay(1.0);
    delay.delayTime.value = 0.268; // ≈ half a beat at 112 BPM
    const delayFeedback = ctx.createGain();
    delayFeedback.gain.value = 0.34;
    const delayDamp = ctx.createBiquadFilter();
    delayDamp.type = 'lowpass';
    delayDamp.frequency.value = 2600;
    const delayReturn = ctx.createGain();
    delayReturn.gain.value = 0.4;
    delaySend.connect(delay);
    delay.connect(delayDamp);
    delayDamp.connect(delayFeedback);
    delayFeedback.connect(delay);
    delay.connect(delayReturn);
    delayReturn.connect(master);

    const families = {} as Record<FamilyName, GainNode>;
    const familySends = {} as Record<FamilyName, GainNode>;
    for (let i = 0; i < FAMILY_NAMES.length; i++) {
      const name = FAMILY_NAMES[i];
      const bus = ctx.createGain();
      bus.gain.value = FAMILY_BUS_GAIN[name];
      bus.connect(groups[FAMILY_GROUP[name]]);
      const send = ctx.createGain();
      send.gain.value = FAMILY_REVERB_SEND[name];
      bus.connect(send);
      send.connect(convolver);
      families[name] = bus;
      familySends[name] = send;
    }

    this.graph = {
      ctx,
      master,
      limiter,
      groups,
      families,
      familySends,
      convolver,
      reverbReturn,
      delaySend,
      delay,
      delayFeedback,
      delayReturn,
      noise: makeWhiteNoise(ctx, 2, 0x9e3779b9),
      brown: makeBrownNoise(ctx, 2, 0x85ebca6b),
    };
    this.applyMasterGain(false);
    this.installFocusGuard();
  }

  /** §13 autoplay policy — arm the first-gesture unlock. Double-guarded. */
  installGestureUnlock(): void {
    if (this.gestureInstalled || this.disposed) return;
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
    this.gestureInstalled = true;
    window.addEventListener('pointerdown', this.onGesture, { passive: true });
    window.addEventListener('touchstart', this.onGesture, { passive: true });
    window.addEventListener('keydown', this.onGesture);
  }

  private removeGestureListeners(): void {
    if (!this.gestureInstalled) return;
    if (typeof window === 'undefined' || typeof window.removeEventListener !== 'function') return;
    window.removeEventListener('pointerdown', this.onGesture);
    window.removeEventListener('touchstart', this.onGesture);
    window.removeEventListener('keydown', this.onGesture);
    this.gestureInstalled = false;
  }

  private installFocusGuard(): void {
    if (this.focusInstalled) return;
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;
    this.focusInstalled = true;
    document.addEventListener('visibilitychange', this.onFocusChange);
  }

  private removeFocusGuard(): void {
    if (!this.focusInstalled) return;
    if (typeof document === 'undefined' || typeof document.removeEventListener !== 'function') return;
    document.removeEventListener('visibilitychange', this.onFocusChange);
    this.focusInstalled = false;
  }

  /** §12.5 sliders. Values are accepted as 0..1 or 0..100. */
  setVolumes(master: number, music: number, sfx: number): void {
    this.volMaster = normalizeVolume(master);
    this.volMusic = normalizeVolume(music);
    this.volSfx = normalizeVolume(sfx);
    const g = this.graph;
    if (!g) return;
    const t = g.ctx.currentTime;
    rampTo(g.groups.music.gain, this.paused ? this.volMusic * PAUSE_MUSIC_LINEAR : this.volMusic, t);
    rampTo(g.groups.sfx.gain, this.paused ? 0 : this.volSfx, t);
    rampTo(g.groups.ambient.gain, this.paused ? 0 : this.volSfx, t);
    this.applyMasterGain(false);
  }

  getVolumes(): { master: number; music: number; sfx: number } {
    return { master: this.volMaster, music: this.volMusic, sfx: this.volSfx };
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.applyMasterGain(false);
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** §12.6 rule (3) — pause mutes sfx/ambient, music holds at −12 dB. */
  setPaused(p: boolean): void {
    this.paused = p;
    const g = this.graph;
    if (!g) return;
    const t = g.ctx.currentTime;
    rampTo(g.groups.sfx.gain, p ? 0 : this.volSfx, t);
    rampTo(g.groups.ambient.gain, p ? 0 : this.volSfx, t);
    rampTo(g.groups.music.gain, p ? this.volMusic * PAUSE_MUSIC_LINEAR : this.volMusic, t);
  }

  private applyMasterGain(forceSilent: boolean): void {
    const g = this.graph;
    if (!g) return;
    const target = this.muted || forceSilent ? 0 : this.volMaster * MASTER_CEILING_LINEAR;
    rampTo(g.master.gain, target, g.ctx.currentTime);
  }

  family(name: FamilyName): GainNode | null {
    return this.graph ? this.graph.families[name] : null;
  }

  suspend(): void {
    const g = this.graph;
    if (!g) return;
    if (g.ctx.state === 'running') void g.ctx.suspend().catch(noop);
  }

  resume(): void {
    const g = this.graph;
    if (!g) return;
    if (g.ctx.state !== 'running') void g.ctx.resume().catch(noop);
  }

  dispose(): void {
    this.removeGestureListeners();
    this.removeFocusGuard();
    const g = this.graph;
    this.graph = null;
    this.initializing = null;
    this.disposed = true;
    if (g) {
      try {
        g.master.disconnect();
        g.limiter.disconnect();
      } catch {
        // Already torn down.
      }
      void g.ctx.close().catch(noop);
    }
  }
}

/** §12.5 — ramp rather than jump so a slider move never clicks; ≤ 50 ms to target. */
export function rampTo(param: AudioParam, value: number, now: number, seconds: number = VOLUME_RAMP_S): void {
  param.cancelScheduledValues(now);
  param.setValueAtTime(param.value, now);
  param.linearRampToValueAtTime(value, now + seconds);
}

export function normalizeVolume(v: number): number {
  if (!Number.isFinite(v)) return 0;
  const x = v > 1 ? v / 100 : v;
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

function noop(): void {
  /* swallow: audio failures never break the game loop */
}
